import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import movementService from '../services/movementService';
import movementTypeService from '../services/movementTypeService';
import { Movement, MovementType } from '../types/Movement';
import MovementForm from '../components/movements/MovementForm';
import ConfirmModal from '../components/common/ConfirmModal';
import ActionButton from '../components/common/ActionButton';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { formatCurrency } from '../utils/format';
import './Movements.css';

const Movements: React.FC = () => {
  const { showToast } = useToast();
  const [movements, setMovements] = useState<Movement[]>([]);
  const [movementTypes, setMovementTypes] = useState<MovementType[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMovement, setEditingMovement] = useState<Movement | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [movementToDelete, setMovementToDelete] = useState<{ id: string; description: string } | null>(null);

  // Filtros
  const [filterInvestmentSheet, setFilterInvestmentSheet] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [filterTipo, setFilterTipo] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [movementsRes, typesRes] = await Promise.all([
        movementService.getAll(),
        movementTypeService.getAll(),
      ]);
      setMovements(movementsRes.data);
      setMovementTypes(typesRes.data);
    } catch (error) {
      console.error('Error loading movements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMovementTypeName = (typeId: string) => {
    const type = movementTypes.find(t => t.id === typeId);
    return type ? type.nombre : typeId;
  };

  const handleCreate = () => {
    setEditingMovement(null);
    setShowForm(true);
  };

  const handleEdit = (movement: Movement) => {
    setEditingMovement(movement);
    setShowForm(true);
  };

  const handleDeleteClick = (id: string, description: string) => {
    setMovementToDelete({ id, description: description || id });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!movementToDelete) return;

    try {
      await movementService.delete(movementToDelete.id);
      showToast('Movimiento eliminado correctamente');
      loadData();
      setShowDeleteModal(false);
      setMovementToDelete(null);
    } catch (error) {
      console.error('Error deleting movement:', error);
      showToast('Error al eliminar el movimiento', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setMovementToDelete(null);
  };

  const clearFilters = () => {
    setFilterInvestmentSheet('');
    setFilterProduct('');
    setFilterTipo('');
  };

  const filterData = (data: Movement[]) => {
    return data.filter((movement) => {
      const matchesInvestmentSheet = !filterInvestmentSheet ||
        movement.investmentSheetId?.toLowerCase().includes(filterInvestmentSheet.toLowerCase());
      const matchesProduct = !filterProduct ||
        movement.productId?.toLowerCase().includes(filterProduct.toLowerCase());
      const typeName = getMovementTypeName(movement.tipoMovimiento);
      const matchesTipo = !filterTipo ||
        typeName.toLowerCase().includes(filterTipo.toLowerCase());
      return matchesInvestmentSheet && matchesProduct && matchesTipo;
    });
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingMovement(null);
    loadData();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingMovement(null);
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const renderTable = () => {
    const data = filterData(movements);

    if (loading) {
      return <div className="loading">Cargando...</div>;
    }

    if (data.length === 0) {
      return <div className="empty-state">No hay movimientos registrados</div>;
    }

    return (
      <table className="movements-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Fecha Valor</th>
            <th>Inversión</th>
            <th>Producto</th>
            <th>Fase</th>
            <th>Tipo</th>
            <th>Entrada</th>
            <th>Salida</th>
            <th>IRPF</th>
            <th>Neto</th>
            <th>Saldo Acum.</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {data.map((movement) => (
            <tr key={movement.id}>
              <td className="id-cell">{movement.id}</td>
              <td>{formatDate(movement.fechaValor)}</td>
              <td>{movement.investmentSheetId}</td>
              <td>{movement.productId}</td>
              <td>{movement.phaseId}</td>
              <td>
                <span className="tipo-badge">
                  {getMovementTypeName(movement.tipoMovimiento)}
                </span>
              </td>
              <td className="currency-cell positive">
                {movement.entrada && movement.entrada > 0 ? formatCurrency(movement.entrada) : '-'}
              </td>
              <td className="currency-cell negative">
                {movement.salida && movement.salida > 0 ? formatCurrency(movement.salida) : '-'}
              </td>
              <td className="currency-cell">{formatCurrency(movement.retencionIrpf)}</td>
              <td className={`currency-cell ${(movement.neto || 0) >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(movement.neto)}
              </td>
              <td className={`currency-cell bold ${(movement.saldoAcumulado || 0) >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(movement.saldoAcumulado)}
              </td>
              <td className="actions">
                {movement.generadoPorAmortizacion ? (
                  <Link to="/amortizaciones" className="movement-locked"
                        title="Generado al ejecutar una amortización anticipada. Para deshacerlo, anule la ejecución en Amortizaciones.">
                    Amortización
                  </Link>
                ) : (
                  <>
                    <ActionButton
                      icon={FaEdit}
                      text="Editar Movimiento"
                      onClick={() => handleEdit(movement)}
                      variant="edit"
                    />
                    <ActionButton
                      icon={FaTrash}
                      text="Eliminar Movimiento"
                      onClick={() => handleDeleteClick(movement.id!, movement.descripcion || movement.id!)}
                      variant="delete"
                    />
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="movements-page">
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar eliminación"
        message={`¿Estás seguro de que deseas eliminar el movimiento ${movementToDelete?.description}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />

      <div className="movements-header">
        <h2>Movimientos</h2>
        <ActionButton
          icon={FaPlus}
          text="Crear Movimiento"
          onClick={handleCreate}
          variant="primary"
        />
      </div>

      {!showForm && (
        <div className="filters-container">
          <div className="filters">
            <div className="filter-group">
              <input
                type="text"
                placeholder="Buscar por inversión..."
                value={filterInvestmentSheet}
                onChange={(e) => setFilterInvestmentSheet(e.target.value)}
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <input
                type="text"
                placeholder="Buscar por producto..."
                value={filterProduct}
                onChange={(e) => setFilterProduct(e.target.value)}
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <input
                type="text"
                placeholder="Buscar por tipo..."
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value)}
                className="filter-input"
              />
            </div>
            {(filterInvestmentSheet || filterProduct || filterTipo) && (
              <button onClick={clearFilters} className="btn-clear-filters">
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      )}

      <div className="movements-content">
        {!showForm ? (
          renderTable()
        ) : (
          <div className="form-container">
            <MovementForm
              movement={editingMovement}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Movements;
